import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { filter, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { ZuiMaterialStackModule } from '@zerops/zui/material-stack';
import { Subject } from 'rxjs';
import { updateIPv4Shared } from '@zerops/zerops/core/project-base/project-base.action';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import { MatLegacySlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { toObservable } from '@angular/core/rxjs-interop';
import { ConfirmPopComponent } from '@zerops/zui/confirm-pop';
import { TranslocoPipe } from '@ngneat/transloco';
import { DynamicPopAnchorModule } from '@zerops/zui/dynamic-pop-anchor';
import { FormsModule } from '@angular/forms';
import { FEATURE_NAME } from './shared-ipv4.constant';
import {
  SharedIPv4Translations,
  SharedIPv4TranslationsModule
} from '@zerops/zerops/feature/shared-ipv4/shared-ipv4.translation';

@Component({
  selector: 'z-shared-ipv4',
  templateUrl: './shared-ipv4.feature.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ZuiMaterialStackModule,
    MatLegacySlideToggleModule,
    ConfirmPopComponent,
    TranslocoPipe,
    DynamicPopAnchorModule,
    FormsModule,
    SharedIPv4TranslationsModule
  ],
})
export class SharedIpv4Container extends ZefReactiveComponent {
  // # Deps
  #projectEntity = inject(ProjectEntity);

  // # Event Streams
  onEnableSharedIPv4$ = new Subject<boolean>();

  // # Data
  // -- angular
  projectId = input<string>();
  isIPv4Enabled = signal<boolean>(false);

  // -- async
  projectId$ = toObservable(this.projectId).pipe(filter((d) => !!d));
  project$ = this.projectId$.pipe(
    switchMap((id) => this.#projectEntity.entityById$(id)),
    filter((id) => !!id)
  );
  translations$ = this.translate$<SharedIPv4Translations>(FEATURE_NAME);
  sharedIPv4$ = this.project$.pipe(
    map((proj) => proj.publicIpV4Shared),
    tap((shared) => this.isIPv4Enabled.set(shared))
  );

  // State resolver
  state = this.$connect({
    translations: this.translations$,
    sharedIPv4: this.sharedIPv4$
  });

  constructor() {
    super();

    this.$dispatchActions([
      this.onEnableSharedIPv4$.pipe(
        withLatestFrom(this.projectId$),
        map(([slideState, projectId]) =>
          updateIPv4Shared({ projectId, enabled: slideState }),
        ),
      ),
    ]);
  }

  changeToggleState(newState: boolean) {
    this.isIPv4Enabled.set(newState);
  }
}
